/**
 * CostEstimator Component
 *
 * Full-width banner showing the estimated cost of the campaign:
 * big number on the left + breakdown chips on the right.
 *
 * Designed to sit just above the SendSummary footer so the marketer
 * sees the price before clicking "Enviar campaña".
 */

import { DollarSign, Users, Tag, Info } from "lucide-react";
import type { WhatsAppTemplate, SegmentSummary } from "../types";

interface CostEstimatorProps {
  segment: SegmentSummary | null;
  template: WhatsAppTemplate | null;
  estimatedCost: number;
}

export function CostEstimator({
  segment,
  template,
  estimatedCost,
}: CostEstimatorProps) {
  if (!segment || !template) return null;

  const recipients = segment.lastKnownCount;
  const ratePerConversation = template.estimatedCostUSD;

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-emerald-50/40 rounded-2xl border border-emerald-100 p-5">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
        {/* Big price block (left) */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="w-10 h-10 bg-white/70 rounded-xl flex items-center justify-center flex-shrink-0">
            <DollarSign size={18} className="text-emerald-600" />
          </div>
          <div>
            <p className="text-[11px] font-medium text-emerald-700 uppercase tracking-wide">
              Costo estimado
            </p>
            <div className="flex items-baseline gap-1.5 mt-0.5">
              <span className="text-3xl font-bold text-slate-900 tracking-tight leading-none">
                ${estimatedCost.toFixed(2)}
              </span>
              <span className="text-xs text-slate-500">USD</span>
            </div>
          </div>
        </div>

        {/* Vertical separator (desktop only) */}
        <div className="hidden lg:block w-px self-stretch bg-emerald-200/60" />

        {/* Breakdown chips (center, takes available space) */}
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 min-w-0">
          <BreakdownChip
            icon={Users}
            label="Destinatarios"
            value={recipients.toLocaleString()}
          />
          <BreakdownChip
            icon={Tag}
            label="Tarifa por conversación"
            value={`$${ratePerConversation.toFixed(4)}`}
          />
        </div>

        {/* Info note (right, only on desktop) */}
        <div className="hidden lg:flex items-start gap-2 px-3 py-2 bg-white/70 rounded-lg max-w-[260px] flex-shrink-0">
          <Info
            size={11}
            className="text-slate-400 mt-0.5 flex-shrink-0"
          />
          <p className="text-[10px] text-slate-500 leading-relaxed">
            Tarifa basada en mercado AR. Meta cobra por conversación de 24h,
            no por mensaje individual.
          </p>
        </div>
      </div>

      {/* Info note (mobile only) */}
      <div className="lg:hidden flex items-start gap-2 px-3 py-2 bg-white/70 rounded-lg mt-3">
        <Info size={11} className="text-slate-400 mt-0.5 flex-shrink-0" />
        <p className="text-[10px] text-slate-500 leading-relaxed">
          Tarifa basada en mercado AR. Meta cobra por conversación de 24h, no
          por mensaje individual.
        </p>
      </div>
    </div>
  );
}

/* ─── Sub-component ─── */
interface BreakdownChipProps {
  icon: typeof Users;
  label: string;
  value: string;
}

function BreakdownChip({ icon: Icon, label, value }: BreakdownChipProps) {
  return (
    <div className="flex items-center gap-2 bg-white/70 rounded-xl px-3 py-2.5 min-w-0">
      <Icon size={14} className="text-slate-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-slate-500 uppercase tracking-wide leading-tight">
          {label}
        </p>
        <p className="text-xs font-mono font-semibold text-slate-900 leading-tight mt-0.5 truncate">
          {value}
        </p>
      </div>
    </div>
  );
}

export default CostEstimator;
